import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { createPrescription, fetchOrder } from "../api";
import {
  User,
  FileUp,
  CircleCheck,
  Package,
  Truck,
  MapPin,
  Home,
  X,
} from "lucide-react";

const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="17" height="17" {...props}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

/* ---------------- LOGIN / REGISTER ---------------- */
const LoginModal = () => {
  const { modal, setModal } = useShop();
  const { loginEmail, registerEmail } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    setModal(null);
    setName("");
    setEmail("");
    setPassword("");
  };

  const submit = async () => {
    if (!email.trim() || !password.trim()) {
      toast.error("Please enter email and password");
      return;
    }
    if (mode === "register" && !name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") {
        await loginEmail(email.trim().toLowerCase(), password);
      } else {
        await registerEmail(name.trim(), email.trim().toLowerCase(), password);
      }
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={modal === "login"} onOpenChange={close}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0e7a3d]">
            <User size={19} /> {mode === "login" ? "Login" : "Create Account"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <button
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-2.5 border border-gray-200 hover:bg-gray-50 rounded-full py-2.5 text-[13.5px] font-semibold text-gray-700 transition-colors"
          >
            <GoogleIcon /> Continue with Google
          </button>
          <div className="flex items-center gap-3 py-0.5">
            <div className="h-px bg-gray-200 flex-1" />
            <span className="text-[11px] text-gray-400">or</span>
            <div className="h-px bg-gray-200 flex-1" />
          </div>
          {mode === "register" && (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13.5px] outline-none focus:border-[#12a150] focus:ring-2 focus:ring-[#12a150]/20"
            />
          )}
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13.5px] outline-none focus:border-[#12a150] focus:ring-2 focus:ring-[#12a150]/20"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Password"
            className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13.5px] outline-none focus:border-[#12a150] focus:ring-2 focus:ring-[#12a150]/20"
          />
          <button
            onClick={submit}
            disabled={busy}
            className="w-full bg-[#12a150] hover:bg-[#0d8a40] disabled:opacity-60 text-white text-[14px] font-bold rounded-full py-2.5 transition-colors"
          >
            {busy ? "Please wait..." : mode === "login" ? "Login" : "Create Account"}
          </button>
          <div className="text-center text-[12px] text-gray-500">
            {mode === "login" ? "New to ArogyaMeds? " : "Already have an account? "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#0e7a3d] font-semibold hover:underline"
            >
              {mode === "login" ? "Register" : "Login"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- UPLOAD PRESCRIPTION ---------------- */
const UploadModal = () => {
  const { modal, setModal } = useShop();
  const [file, setFile] = useState(null);
  const [done, setDone] = useState(false);

  const close = () => {
    setModal(null);
    setTimeout(() => {
      setFile(null);
      setDone(false);
    }, 300);
  };

  const submit = async () => {
    if (!file) {
      toast.error("Please choose a prescription file first");
      return;
    }
    try {
      await createPrescription({ file_name: file.name });
    } catch {
      /* offline demo fallback */
    }
    setDone(true);
  };

  return (
    <Dialog open={modal === "upload"} onOpenChange={close}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0e7a3d]">
            <FileUp size={19} /> Upload Prescription
          </DialogTitle>
        </DialogHeader>
        {!done ? (
          <div className="space-y-3 pt-1">
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-[#12a150] rounded-xl py-8 cursor-pointer transition-colors">
              <FileUp size={30} className="text-[#12a150] mb-2" />
              <span className="text-[13px] font-semibold text-gray-700">
                {file ? file.name : "Click to choose prescription"}
              </span>
              <span className="text-[11px] text-gray-400 mt-1">
                JPG, PNG or PDF · Max 5MB
              </span>
              <input
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            {file && (
              <div className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2">
                <span className="text-[12px] text-gray-700 truncate max-w-[260px]">
                  {file.name}
                </span>
                <button onClick={() => setFile(null)} className="text-gray-400 hover:text-red-500">
                  <X size={14} />
                </button>
              </div>
            )}
            <button
              onClick={submit}
              className="w-full bg-[#12a150] hover:bg-[#0d8a40] text-white text-[14px] font-bold rounded-full py-2.5 transition-colors"
            >
              Submit Prescription
            </button>
          </div>
        ) : (
          <div className="py-6 flex flex-col items-center text-center">
            <CircleCheck size={52} className="text-[#12a150] mb-3" />
            <div className="text-[16px] font-bold text-gray-800">Prescription uploaded!</div>
            <div className="text-[12.5px] text-gray-500 mt-1.5 leading-relaxed">
              Our licensed pharmacist will verify it and call you
              <br />
              within 30 minutes.
            </div>
            <button
              onClick={close}
              className="mt-4 bg-[#12a150] hover:bg-[#0d8a40] text-white text-[13px] font-bold rounded-full px-8 py-2 transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- TRACK ORDER ---------------- */
import { mockOrderSteps } from "../mock";

const stepIcons = [Package, Package, Truck, MapPin, Home];

const TrackModal = () => {
  const { modal, setModal } = useShop();
  const [orderId, setOrderId] = useState("AGM-849201");
  const [tracked, setTracked] = useState(false);
  const [steps, setSteps] = useState(mockOrderSteps);

  const track = async () => {
    if (!orderId.trim()) {
      toast.error("Please enter an order ID");
      return;
    }
    try {
      const order = await fetchOrder(orderId.trim());
      setSteps(order?.steps?.length ? order.steps : mockOrderSteps);
    } catch {
      setSteps(mockOrderSteps);
    }
    setTracked(true);
  };

  return (
    <Dialog open={modal === "track"} onOpenChange={() => setModal(null)}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0e7a3d]">
            <MapPin size={19} /> Track Order
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 pt-1">
          <input
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="Enter order ID"
            className="flex-1 border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13.5px] outline-none focus:border-[#12a150] focus:ring-2 focus:ring-[#12a150]/20"
          />
          <button
            onClick={track}
            className="bg-[#12a150] hover:bg-[#0d8a40] text-white text-[13px] font-bold rounded-full px-5 transition-colors"
          >
            Track
          </button>
        </div>
        {tracked && (
          <div className="pt-4 pb-1">
            <div className="text-[12px] text-gray-500 mb-3">
              Order <span className="font-bold text-gray-800">{orderId}</span> · Arriving today
            </div>
            <div className="space-y-0">
              {steps.map((s, i) => {
                const Icon = stepIcons[i] || Package;
                const last = i === steps.length - 1;
                return (
                  <div key={s.label} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          s.done
                            ? "bg-[#12a150] text-white"
                            : s.active
                            ? "bg-white border-2 border-[#12a150] text-[#12a150]"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        <Icon size={14} />
                      </div>
                      {!last && (
                        <div className={`w-0.5 h-7 ${s.done ? "bg-[#12a150]" : "bg-gray-200"}`} />
                      )}
                    </div>
                    <div className="pt-1.5">
                      <div
                        className={`text-[13px] font-semibold ${
                          s.done || s.active ? "text-gray-800" : "text-gray-400"
                        }`}
                      >
                        {s.label}
                        {s.active && (
                          <span className="ml-2 text-[10px] font-bold text-[#0e7a3d] bg-green-50 px-2 py-0.5 rounded-full">
                            LIVE
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-400">{s.time}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Modals = () => (
  <>
    <LoginModal />
    <UploadModal />
    <TrackModal />
  </>
);

export default Modals;
